import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { itineraryApi } from '@/src/api/rtkQuery/itineraryApi'
import type { ShukranDetail } from '@/src/types/couponTray'

export interface ShukranFareInput {
    fare_key: string
    price: number
    fare_breakup: {
        BF: number
        TAX: number
    }
}

export interface ApplyShukranPayload {
    fares: ShukranFareInput[]
    program_name: 'SHUKRAN'
    cardNumber: string
}

interface ShukranServiceResponse {
    message?: string
    fares?: Record<string, number | string>
}

export const shukranApi = createApi({
    reducerPath: 'shukranApi',
    baseQuery: fetchBaseQuery({
        credentials: 'include',
        prepareHeaders: headers => {
            headers.set('Content-Type', 'application/json')
            return headers
        }
    }),
    endpoints: builder => ({
        applyShukran: builder.mutation<
            ShukranDetail | null,
            { itineraryId: string; fareKey: string; payload: ApplyShukranPayload }
        >({
            query: ({ payload }) => ({
                url: '/freqFlyerService/v1/fareKeys/loyaltyRewardsPoint',
                method: 'POST',
                body: payload
            }),
            transformResponse: (response: ShukranServiceResponse, meta, arg) => {
                const rawPoints = response?.fares?.[arg.fareKey]
                const loyaltyPoints = rawPoints == null ? 0 : Number(rawPoints)
                const successMessageTemplate = response?.message || ''
                const successMessage = successMessageTemplate.includes('${POINTS}')
                    ? successMessageTemplate.replace('${POINTS}', String(loyaltyPoints))
                    : successMessageTemplate

                return {
                    cardNumber: arg.payload.cardNumber,
                    loyaltyPoints,
                    successMessage,
                    status: 'SUCCESS'
                }
            },
            async onQueryStarted({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(
                        itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                            if (draft?.itineraryData) {
                                draft.itineraryData.shukranData = data
                            }
                        })
                    )
                } catch (e) {
                    console.error('Failed to update Shukran cache after applyShukran:', e)
                }
            }
        }),
        clearShukran: builder.mutation<
            { success: boolean },
            { itineraryId: string }
        >({
            queryFn: async () => ({ data: { success: true } }),
            async onQueryStarted({ itineraryId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                            if (draft?.itineraryData) {
                                draft.itineraryData.shukranData = null
                            }
                        })
                    )
                } catch (e) {
                    console.error('Failed to clear Shukran cache:', e)
                }
            }
        })
    })
})

export const { useApplyShukranMutation, useClearShukranMutation } = shukranApi
export default shukranApi
